import { formatApiError } from "../../utils/helpers/apiErrors";

// The timetabling endpoints answer a rejected write with a machine-readable
// body: {code, detail, details}. Branch on `code` — never on the English prose
// (see LessonEditorModal, which regex-matches `detail` and mislabels any new
// conflict type). `formatApiError` must NOT be used on these bodies directly:
// it takes Object.keys(data)[0] === "code" and renders "code: already_running".
//
// Shared by every timetable surface (generate, board, commit). Extend the map
// when the backend grows a code; the fallback chain keeps an unmapped code
// readable in the meantime.

// code -> i18n key. Mirrors GenerationError codes in
// organization/services/generation.py plus the cancel view's not_in_flight and
// the TimetableConflict codes that come back inside commit's `skipped` rows.
const CODE_KEYS = {
  already_running: "timetable.errAlreadyRunning",
  not_reviewable: "timetable.errNotReviewable",
  empty_board: "timetable.errEmptyBoard",
  not_in_flight: "timetable.errNotInFlight",
  slot_taken: "timetable.errSlotTaken",
  invalid_slot: "timetable.errInvalidSlot",
  class_slot_taken: "timetable.errClassSlotTaken",
  teacher_slot_taken: "timetable.errTeacherSlotTaken",
  cross_org_slot: "timetable.errCrossOrgSlot",
};

/** The `code` of a timetabling error body, or null for a plain DRF error. */
export function apiErrorCode(err) {
  const raw = err?.response?.data?.code;
  // BOTH shapes occur. A view that builds the body itself sends a bare string;
  // a serializer raising ValidationError({..., "code": "x"}) has DRF wrap it in
  // a list. Reading only the string silently dropped every serializer-raised
  // code on the floor and printed the English sentence instead.
  const code = Array.isArray(raw) ? raw[0] : raw;
  return typeof code === "string" && code ? code : null;
}

/** The `details` array (e.g. the in-flight run uuid for `already_running`). */
export function apiErrorDetails(err) {
  const details = err?.response?.data?.details;
  return Array.isArray(details) ? details : [];
}

/** Localized label for a known code, or null when we have no string for it. */
export function codeMessage(code, t) {
  const key = CODE_KEYS[code];
  return key ? t(key) : null;
}

/**
 * Toast-able message for any timetabling failure.
 * code -> localized string, else the server's `detail`, else the DRF-shaped
 * field errors, else `fallback`.
 */
export function runErrorMessage(err, t, fallback) {
  const mapped = codeMessage(apiErrorCode(err), t);
  if (mapped) return mapped;
  const detail = err?.response?.data?.detail;
  if (typeof detail === "string" && detail) return detail;
  return formatApiError(err, fallback);
}
